import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Semantic status tokens for the sparkline stroke. Mapped internally to the same
 * CSS variables brand-wide status colors use, so sparklines adapt automatically
 * to dark mode.
 */
export type SparklineStatus = 'on-track' | 'success' | 'warning' | 'danger' | 'neutral' | 'muted';

/** Size variants controlling stroke width; dimensions are controlled by the parent. */
export type SparklineSize = 'xs' | 'sm' | 'md';

/**
 * Inline mini trend line. Renders an SVG polyline across its container, clamped to
 * a 0–100 value range. Use for stat-card trailing trends, row-level trend cells, or
 * any dense view where a full chart would be overkill. For standalone charts, use
 * the `chart/line-chart` wrapper instead.
 *
 * Colors are chosen from a semantic status union so dark mode and brand consistency
 * are handled automatically. An escape hatch (`customColor`) is provided for callers
 * that genuinely need an unusual token, but prefer the status union in new code.
 *
 * @example
 * <!-- Row trend line -->
 * <div class="w-16 h-10">
 *   <app-sparkline [plotPoints]="points" status="on-track" size="md" />
 * </div>
 *
 * @example
 * <!-- Tiny inline sparkline -->
 * <div class="w-8 h-3 shrink-0">
 *   <app-sparkline [plotPoints]="points" status="success" size="xs" ariaLabel="7-day trend, improving" />
 * </div>
 *
 * @feature shared
 */
@Component({
  selector: 'app-sparkline',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block w-full h-full' },
  template: `
    <svg
      viewBox="0 0 100 40"
      preserveAspectRatio="none"
      class="w-full h-full"
      [attr.role]="ariaLabel() ? 'img' : 'presentation'"
      [attr.aria-label]="ariaLabel()"
      [attr.aria-hidden]="ariaLabel() ? null : 'true'"
      focusable="false"
    >
      @if (hasLine()) {
        <polyline
          [attr.points]="points()"
          fill="none"
          [attr.stroke]="strokeVar()"
          [attr.stroke-width]="strokeWidth()"
          stroke-linecap="round"
          stroke-linejoin="round"
          vector-effect="non-scaling-stroke"
        />
      }
    </svg>
  `,
})
export class SparklineComponent {
  /** Plot points on a 0–100 scale, oldest → newest. */
  readonly plotPoints = input<number[]>([]);

  /** Semantic status driving the stroke color. @default 'neutral' */
  readonly status = input<SparklineStatus>('neutral');

  /** Size variant. Controls stroke width. @default 'sm' */
  readonly size = input<SparklineSize>('sm');

  /**
   * Escape-hatch CSS color string (any valid CSS color or `var(...)`). When set,
   * overrides `status`. Prefer the status union in new code.
   */
  readonly customColor = input<string | undefined>(undefined);

  /**
   * Accessible label. Required when the sparkline stands alone (no adjacent text).
   * When omitted, the sparkline is marked `aria-hidden="true"` and treated as
   * decorative.
   */
  readonly ariaLabel = input<string | undefined>(undefined);

  /** True when there are at least two points to render a line. */
  readonly hasLine = computed(() => this.plotPoints().length >= 2);

  /** Stroke width per size variant. */
  readonly strokeWidth = computed(() => {
    switch (this.size()) {
      case 'xs':
        return 1.5;
      case 'md':
        return 2.5;
      case 'sm':
      default:
        return 2;
    }
  });

  /** Resolved CSS color. Honors `customColor` first, then the status union. */
  readonly strokeVar = computed(() => {
    const custom = this.customColor();
    if (custom) return custom;
    switch (this.status()) {
      case 'on-track':
        return 'var(--ion-color-primary)';
      case 'success':
        return 'var(--ion-color-success)';
      case 'warning':
        return 'var(--ion-color-warning)';
      case 'danger':
        return 'var(--ion-color-danger)';
      case 'muted':
        return 'var(--ion-color-text-muted)';
      case 'neutral':
      default:
        return 'var(--ion-color-medium)';
    }
  });

  /** Computed SVG polyline `points` attribute. */
  readonly points = computed(() => {
    const raw = this.plotPoints();
    if (!raw || raw.length === 0) return '';

    const width = 100;
    const height = 40;
    const padding = 2;
    const n = raw.length;
    if (n === 1) {
      // Single point: render as a short horizontal tick at the correct y so it is still visible.
      const v = Math.max(0, Math.min(100, raw[0]));
      const y = height - (v / 100) * (height - padding * 2) - padding;
      return `0,${y} ${width},${y}`;
    }

    const parts: string[] = [];
    for (let i = 0; i < n; i++) {
      const v = Math.max(0, Math.min(100, raw[i]));
      const x = (i / (n - 1)) * width;
      const y = height - (v / 100) * (height - padding * 2) - padding;
      parts.push(`${x},${y}`);
    }
    return parts.join(' ');
  });
}
